<template>
  <div class="main-wrapper">
    <hr>
    <el-row type="flex" class="mb-1 align-center">
      <b class="section-subheader">SMS Attachment Upload Preferences</b>
    </el-row>

    <!-- Start show error banner if missing scopes -->
    <template v-if="hasError">
      <div class="p-3 container mt-3 mb-4">
        <i class="el-icon-warning-outline mr-1"></i>
        <span>
          {{ errorMessage }}
        </span>
      </div>
    </template>
    <!-- End show error banner if missing scopes-->

    <!-- Start show toggle and selector if scopes are available -->
    <template v-else>
      <el-row>
        <p class="mt-2">
          When you enabled, SMS attachments will <strong>automatically be uploaded</strong> to the HubSpot folder you choose.
          For better organization, a new, separate subfolder will be created inside your chosen folder for every contact, and each subfolder will be named using that contact's unique <strong>Aloware contact ID</strong>.
        </p>
      </el-row>
      <el-row>
        <el-switch
          v-model="smsAttachmentUploadStatus"
          active-color="#00a344">
        </el-switch>
      </el-row>

      <template v-if="smsAttachmentUploadStatus">
        <!-- Start Folder Selector -->
        <el-row class="mt-3">
          <el-col :span="24">
            <label class="folder-label">Select HubSpot Folder</label>
            <el-select
              v-model="selectedFolderId"
              class="w-full mt-2"
              filterable
              remote
              reserve-keyword
              remote-show-suffix
              placeholder="Select a folder"
              :loading="loadingFolders"
              :disabled="loadingFolders"
              :remote-method="searchFolders">
              <el-option
                v-for="folder in availableFolders"
                :key="folder.id"
                :label="folder.name + '      ' + folder.path"
                :value="folder.id">
                <span>{{ folder.name }}</span>
                <span style="float: right; color: #8492a6; font-size: 13px">{{ folder.path }}</span>
              </el-option>
            </el-select>
          </el-col>
        </el-row>
        <!-- End Folder Selector -->

        <!-- Overwrite Files Toggle -->
        <el-row style="display: flex; justify-content: space-between; align-items: center; margin-top: 20px;">
          <div style="display: flex; flex-direction: column; justify-content: center; width: 100%;">
            <b>Overwrite Existing Files</b>
            <span>Enable to replace files with the same name and extension in a contact's folder.</span>
          </div>
          <el-switch
            v-model="overwriteEnabled"
            active-color="#00a344">
          </el-switch>
        </el-row>
        <!-- End Overwrite Files Toggle -->
      </template>
    </template>
    <!-- End show toggle and selector if scopes are available -->
  </div>
</template>

<script>
import _ from 'lodash'

export default {
  props: {
    model: {
      type: Object,
      required: true
    },
    defaultFileUploadSettings: {
      type: Object,
      required: false
    },
  },
  data() {
    return {
      availableFolders: [],
      loadingFolders: false,
      hasError: false,
      errorMessage: ''
    }
  },
  computed: {
    smsAttachmentUploadStatus: {
      get() {
        return this.model.settings.file_upload_settings?.sms_attachments?.enabled ?? false
      },
      set(value) {
        this.handleSmsAttachmentUploadStatusChange(value)
      }
    },
    selectedFolderId: {
      get() {
        return this.model.settings.file_upload_settings?.sms_attachments?.folder_id ?? null
      },
      set(value) {
        this.handleFolderSelection(value)
      }
    },
    overwriteEnabled: {
      get() {
        return this.model.settings.file_upload_settings?.sms_attachments?.overwrite ?? false
      },
      set(value) {
        this.handleOverwriteChange(value)
      }
    }
  },
  async created() {
    // Initialize sms_attachments if it doesn't exist (for accounts created before this feature)
    if (!this.model.settings.file_upload_settings?.sms_attachments && this.defaultFileUploadSettings?.sms_attachments) {
      this.$set(this.model.settings, 'file_upload_settings', {
        sms_attachments: _.cloneDeep(this.defaultFileUploadSettings.sms_attachments)
      })
    }

    // If there's a currently selected folder, add it to the list so it shows up in the dropdown
    const currentFolder = this.model.settings.file_upload_settings?.sms_attachments
    if (currentFolder?.folder_id && currentFolder?.folder_name && currentFolder?.folder_path) {
      this.availableFolders = [{
        id: currentFolder.folder_id,
        name: currentFolder.folder_name,
        path: currentFolder.folder_path
      }]
    }

    await this.fetchFolders('aloware') // assume to check for an aloware folder at the start, this will also trigger if the account has the correct scopes
  },
  methods: {
    async fetchFolders(query = '') {
      try {
        this.loadingFolders = true
        const params = query ? { name: query } : {}
        const response = await axios.get('/api/v1/integration/hubspot/folders', { params })
        if (response.data.success) {
          const searchResults = response.data.data

          // Get the currently selected folder
          const currentFolder = this.model.settings.file_upload_settings?.sms_attachments
          const currentFolderId = currentFolder?.folder_id

          // If there's a selected folder, and it's not in the search results, add it to the list
          if (currentFolderId && currentFolder.folder_name && currentFolder.folder_path) {
            const selectedFolderInResults = searchResults.find(f => f.id === currentFolderId)
            if (!selectedFolderInResults) {
              this.availableFolders = [
                {
                  id: currentFolder.folder_id,
                  name: currentFolder.folder_name,
                  path: currentFolder.folder_path
                },
                ...searchResults
              ]
            } else {
              this.availableFolders = searchResults
            }
          } else {
            this.availableFolders = searchResults
          }
        }
      } catch (error) {
        if (error.response?.status === 400) {
          this.hasError = true
          this.errorMessage = error.response?.data?.message || 'An error occurred while loading folders'
        } else {
          console.error('Error fetching folders:', error)
          this.hasError = true
          this.errorMessage = 'Failed to load HubSpot folders'
        }
      } finally {
        this.loadingFolders = false
      }
    },
    searchFolders: _.debounce(function(query) {
      if (query) {
        this.fetchFolders(query)
      }
    }, 800),
    handleSmsAttachmentUploadStatusChange(newVal) {
      // Ensure the structure exists
      if (!this.model.settings.file_upload_settings) {
        this.$set(this.model.settings, 'file_upload_settings', {})
      }
      if (!this.model.settings.file_upload_settings.sms_attachments) {
        this.$set(this.model.settings.file_upload_settings, 'sms_attachments', {
          enabled: false,
          folder_id: null,
          folder_name: null,
          folder_path: null,
          overwrite: false
        })
      }

      // Update the enabled status
      this.$set(this.model.settings.file_upload_settings.sms_attachments, 'enabled', newVal)

      // If disabling, clear folder selection and set overwrite to false
      if (!newVal) {
        this.$set(this.model.settings.file_upload_settings.sms_attachments, 'folder_id', null)
        this.$set(this.model.settings.file_upload_settings.sms_attachments, 'folder_name', null)
        this.$set(this.model.settings.file_upload_settings.sms_attachments, 'folder_path', null)
        this.$set(this.model.settings.file_upload_settings.sms_attachments, 'overwrite', false)
      }
    },
    handleFolderSelection(folderId) {
      if (!folderId) {
        if (this.model.settings.file_upload_settings?.sms_attachments) {
          this.$set(this.model.settings.file_upload_settings.sms_attachments, 'folder_id', null)
          this.$set(this.model.settings.file_upload_settings.sms_attachments, 'folder_name', null)
          this.$set(this.model.settings.file_upload_settings.sms_attachments, 'folder_path', null)
        }
        return
      }

      const selectedFolder = this.availableFolders.find(folder => folder.id === folderId)

      if (selectedFolder) {
        // Ensure the structure exists
        if (!this.model.settings.file_upload_settings) {
          this.$set(this.model.settings, 'file_upload_settings', {})
        }
        if (!this.model.settings.file_upload_settings.sms_attachments) {
          this.$set(this.model.settings.file_upload_settings, 'sms_attachments', {
            enabled: true,
            folder_id: null,
            folder_name: null,
            folder_path: null,
            overwrite: false
          })
        }

        // Update the model with selected folder details
        this.$set(this.model.settings.file_upload_settings.sms_attachments, 'folder_id', selectedFolder.id)
        this.$set(this.model.settings.file_upload_settings.sms_attachments, 'folder_name', selectedFolder.name)
        this.$set(this.model.settings.file_upload_settings.sms_attachments, 'folder_path', selectedFolder.path)
      }
    },
    handleOverwriteChange(value) {
      // Ensure the structure exists
      if (!this.model.settings.file_upload_settings) {
        this.$set(this.model.settings, 'file_upload_settings', {})
      }
      if (!this.model.settings.file_upload_settings.sms_attachments) {
        this.$set(this.model.settings.file_upload_settings, 'sms_attachments', {
          enabled: false,
          folder_id: null,
          folder_name: null,
          folder_path: null,
          overwrite: false
        })
      }

      // Update the overwrite status using $set for reactivity
      this.$set(this.model.settings.file_upload_settings.sms_attachments, 'overwrite', value)
    }
  }
}
</script>

<style scoped>
.main-wrapper {
  width: 100%;
}

.section-subheader {
  font-size: 16px;
}

.align-center {
  align-items: center;
}

.container {
  border: 1px solid grey;
  border-radius: 5px;
  width: 100%;
  max-width: 100%;
}

.folder-label {
  font-weight: 600;
  font-size: 14px;
  color: #303133;
}

.w-full {
  width: 100%;
}

.mt-2 {
  margin-top: 0.5rem;
}

.mt-3 {
  margin-top: 1rem;
}

.mt-4 {
  margin-top: 1.5rem;
}

.mb-1 {
  margin-bottom: 0.25rem;
}

.mb-4 {
  margin-bottom: 1.5rem;
}

.mr-1 {
  margin-right: 0.25rem;
}

.p-3 {
  padding: 1rem;
}
</style>
